/**
 * IXD Preview 비교(렌더 뷰) 전역 상태 스토어.
 *
 * NDJSON 스트림으로 도착하는 summary/TOC 를 누적한다. 페이지 스코프로 동작하며
 * Provider 언마운트 시 초기화된다. 갈래 B(ixd-ixd-compare)와는 데이터 형태가 달라
 * 별도 스토어를 둔다.
 */

import { createContext, useContext } from "react";
import { createStore, useStore } from "zustand";
import type { PreviewDiffTocChunk } from "@/apis/compare/route";

export type IxdPreviewCompareStatus = "idle" | "streaming" | "done" | "error";

export type IxdPreviewCompareState = {
  status: IxdPreviewCompareStatus;
  error: string | null;
  tocs: PreviewDiffTocChunk[];
  /** 헤더에 표시할 기준/비교 소스 라벨(업로드 파일명 등). 입력 시점에만 알 수 있다. */
  srcLabel: string | null;
  trgLabel: string | null;
  activeSection: string | null;
  selectedCellId: string | null;
  start: () => void;
  addToc: (toc: PreviewDiffTocChunk) => void;
  setSources: (srcLabel: string, trgLabel: string) => void;
  finish: () => void;
  fail: (message: string) => void;
  setActiveSection: (id: string | null) => void;
  setSelectedCellId: (id: string | null) => void;
};

const INITIAL = {
  status: "idle" as IxdPreviewCompareStatus,
  error: null,
  tocs: [] as PreviewDiffTocChunk[],
  srcLabel: null,
  trgLabel: null,
  activeSection: null,
  selectedCellId: null,
};

export const createIxdPreviewCompareStore = () =>
  createStore<IxdPreviewCompareState>()((set) => ({
    ...INITIAL,
    start: () => set({ ...INITIAL, status: "streaming" }),
    addToc: (toc) => set((state) => ({ tocs: [...state.tocs, toc] })),
    setSources: (srcLabel, trgLabel) => set({ srcLabel, trgLabel }),
    finish: () =>
      set((state) => ({
        status: state.status === "error" ? "error" : "done",
      })),
    fail: (message) => set({ status: "error", error: message }),
    setActiveSection: (activeSection) => set({ activeSection }),
    setSelectedCellId: (selectedCellId) => set({ selectedCellId }),
  }));

export type IxdPreviewCompareStore = ReturnType<
  typeof createIxdPreviewCompareStore
>;

export const IxdPreviewCompareStoreContext =
  createContext<IxdPreviewCompareStore | null>(null);

/** 페이지 내 전역 상태 구독. Provider 밖에서 사용 시 에러를 던진다. */
export const useIxdPreviewCompareStore = <T>(
  selector: (state: IxdPreviewCompareState) => T,
): T => {
  const store = useContext(IxdPreviewCompareStoreContext);
  if (!store) {
    throw new Error(
      "useIxdPreviewCompareStore must be used within IxdPreviewCompareStoreProvider",
    );
  }
  return useStore(store, selector);
};
